// Modal confirmation dialog for destructive sidebar actions: clearing all
// chats/graphs, and deleting the bulk selection.

import React, { useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';

interface ClearAllDialogProps {
  onConfirm: () => void;
  onClose: () => void;
  label?: string;
  /** Overrides the default "clear all {label}" question. */
  message?: string;
  /** Overrides the default "Clear All" confirm button text. */
  confirmLabel?: string;
}

export default function ClearAllDialog({
  onConfirm,
  onClose,
  label = 'chats',
  message,
  confirmLabel = 'Clear All',
}: ClearAllDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Focus the safe choice so Enter doesn't destroy anything by accident.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Close on overlay click
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="malpedia-clear-all-overlay"
      onClick={handleOverlayClick}
    >
      <div className="malpedia-clear-all-modal" role="alertdialog" aria-modal="true" aria-label={confirmLabel}>
        <p className="malpedia-clear-all-modal-text">
          {message ?? `Are you sure you want to clear all ${label}? This action cannot be undone.`}
        </p>
        <div className="malpedia-clear-all-modal-actions">
          <button
            className="malpedia-clear-all-modal-btn malpedia-clear-all-modal-btn--danger"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
          <button
            ref={cancelRef}
            className="malpedia-clear-all-modal-btn"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
